import { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CookieIcon from '@mui/icons-material/Cookie';

const STORAGE_KEY = 'sa_cookie_consent';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay so it doesn't appear instantly on page load
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              bgcolor: '#0F1D2E',
              borderTop: '1px solid rgba(209,188,138,0.25)',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.35)',
              px: { xs: 2, sm: 4 },
              py: { xs: 2.5, sm: 2 },
            }}
          >
            <Box
              sx={{
                maxWidth: 'lg',
                mx: 'auto',
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: { xs: 2, md: 4 },
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              {/* Icon + text */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                <CookieIcon sx={{ color: '#D1BC8A', fontSize: 22, mt: 0.2, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, fontSize: '0.85rem' }}>
                  Wir verwenden Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten und unsere Website
                  kontinuierlich zu verbessern. Weitere Informationen finden Sie in unserer{' '}
                  <Link
                    component={RouterLink}
                    to="/datenschutz"
                    sx={{ color: '#D1BC8A', fontWeight: 600, textDecorationColor: 'rgba(209,188,138,0.4)' }}
                  >
                    Datenschutzerklärung
                  </Link>.
                </Typography>
              </Box>

              {/* Buttons */}
              <Stack direction="row" spacing={1.5} flexShrink={0}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={decline}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.75,
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.4)', color: '#fff' },
                  }}
                >
                  Ablehnen
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={accept}
                  sx={{
                    background: 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)',
                    color: '#0F1D2E',
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    px: 2.5,
                    py: 0.75,
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 14px rgba(209,188,138,0.3)',
                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(209,188,138,0.4)' },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Alle akzeptieren
                </Button>
              </Stack>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
